import { ChainOfThoughtSearchResult, ChainOfThoughtSearchResults } from '@newfront-insurance/nova-core';
import type { ComponentType } from 'react';

import { type CoTSearchResult, getField, MAX_OUTPUT_LENGTH } from '../lib/chain-of-thought-utils';

// TODO: Export typed tool input/output interfaces from the backend so renderers
// can use concrete types instead of `unknown` + runtime field access via getField.
type CoTToolOutputRendererProps = { output: unknown };

/** Registry of custom per-tool output renderers */
const COT_TOOL_OUTPUT_RENDERERS: Record<string, ComponentType<CoTToolOutputRendererProps>> = {
  documentSearch: ({ output }) => {
    const summary = getField<string>(output, 'searchSummary');
    return summary ? <p>{summary}</p> : null;
  },
  dataExplorer: ({ output }) => {
    const reasoning = getField<string>(output, 'reasoning');
    return reasoning ? <p>{reasoning}</p> : null;
  },
};

type CoTToolInputRendererProps = { input: unknown };

/** Registry of custom per-tool input renderers (shown next to the step label) */
const COT_TOOL_INPUT_RENDERERS: Record<string, ComponentType<CoTToolInputRendererProps>> = {
  documentSearch: ({ input }) => {
    const query = getField<string>(input, 'query');
    return query ? <span>Searching for &ldquo;{query}&rdquo;</span> : null;
  },
  processFiles: ({ input }) => {
    const context = getField<string>(input, 'context');
    return context ? <span>{context}</span> : null;
  },
  dataExplorer: ({ input }) => {
    const reasoning = getField<string>(input, 'lastMessageReasoning');
    return reasoning ? <span>{reasoning}</span> : null;
  },
};

/** Registry of extractors that pull search results from tool output */
const COT_TOOL_SEARCH_RESULT_EXTRACTORS: Record<string, (output: unknown) => CoTSearchResult[]> = {
  documentSearch: (output) => {
    const results = getField<Array<Record<string, unknown>>>(output, 'results');
    if (!Array.isArray(results)) return [];
    return results
      .filter((r): r is Record<string, unknown> & { filename: string } => typeof r.filename === 'string')
      .map((r) => ({ title: r.filename, link: typeof r.navigatorUrl === 'string' ? r.navigatorUrl : undefined }));
  },
  dataExplorer: (output) => {
    const rowCount = getField<number>(output, 'data', 'rowCount');
    if (rowCount == null || rowCount === 0) return [];
    return [{ title: `${rowCount} result${rowCount === 1 ? '' : 's'} found` }];
  },
};

/** Renders tool input as a description for a CoT step — returns null when no custom renderer exists */
export function CoTToolInput({ toolName, input }: { toolName: string; input: unknown }) {
  const CustomRenderer = COT_TOOL_INPUT_RENDERERS[toolName];
  if (CustomRenderer) return <CustomRenderer input={input} />;
  return null;
}

/** Renders search result badges from tool output — returns null when no extractor exists or no results */
export function CoTToolSearchResults({ toolName, output }: { toolName: string; output: unknown }) {
  const extractor = COT_TOOL_SEARCH_RESULT_EXTRACTORS[toolName];
  if (!extractor) return null;
  const results = extractor(output);
  if (results.length === 0) return null;
  return (
    <ChainOfThoughtSearchResults>
      {results.map((result, index) =>
        result.link ? (
          <a key={result.link} href={result.link} target="_blank" rel="noopener noreferrer">
            <ChainOfThoughtSearchResult>{result.title}</ChainOfThoughtSearchResult>
          </a>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <ChainOfThoughtSearchResult key={index}>{result.title}</ChainOfThoughtSearchResult>
        ),
      )}
    </ChainOfThoughtSearchResults>
  );
}

/** Renders tool output in a CoT step — uses custom renderer if available, falls back to truncated string */
export function CoTToolOutput({ toolName, output }: { toolName: string; output: unknown }) {
  const CustomRenderer = COT_TOOL_OUTPUT_RENDERERS[toolName];
  if (CustomRenderer) return <CustomRenderer output={output} />;

  if (typeof output === 'string') {
    const text = output.length > MAX_OUTPUT_LENGTH ? `${output.slice(0, MAX_OUTPUT_LENGTH)}…` : output;
    return <p>{text}</p>;
  }

  return null;
}
